import { labelBinary } from "./labeling";
import { PRESETS } from "./presets";
import type { BinaryLabel, Candle, Preset, PresetResolution, Round } from "../types";

/**
 * The answer candle of a Next-Candle Round played over a barrier bank: the
 * first Forward candle. The daily bank stores a deep Forward path for the
 * trend game, and the next-candle game reads only its first step, so those
 * two horizons share one bank (CONTEXT.md "Loop").
 */
export function nextCandleOf(round: Round): Candle {
  return round.forward[0];
}

/**
 * Green/red of a Next-Candle answer candle. Unlike the weekly and monthly
 * banks, a barrier bank is not filtered for exact-flat candles (nothing else
 * needs it to be), so the vanishingly rare `close === open` is read as green
 * rather than left unresolved.
 */
export function candleDirection(candle: Candle): BinaryLabel {
  return candle.close >= candle.open ? "up" : "down";
}

/** Player-facing Green/Red text for a binary label, the Next-Candle Loops' shared vocabulary. */
export function greenRedLabel(label: BinaryLabel): string {
  return label === "up" ? "Green" : "Red";
}

/**
 * Resolve a Round for the active difficulty preset (spec §7).
 *
 * Predict is Higher/Lower only: the label is derived live via the labeling
 * library's binary variant (the time cap collapses to the sign of the net
 * move). The rare exact-flat case (labelBinary → null) falls back to the sign
 * of the stored resolution's % move, so a played Round always resolves to Up
 * or Down. The stored per-preset resolution still supplies the barriers and
 * the numbers the reveal quotes (ADR-0004 guarantees one exists per preset).
 */
export function resolveRound(round: Round, preset: Preset): PresetResolution {
  const stored = round.resolutions[preset];

  const binary = labelBinary(round.context, round.forward, PRESETS[preset]);
  if (binary) return binary;
  return { ...stored, label: stored.pctMove >= 0 ? "up" : "down" };
}
